import { Injectable } from '@angular/core';
import { Translations, SupportedLocale } from '../types/localization.types';
import { en } from '../i18n/en';
import { tr } from '../i18n/tr';
import { nl } from '../i18n/nl';

/**
 * Service for managing translations in the PDF Form Designer
 */
@Injectable({
  providedIn: 'root'
})
export class LocalizationService {
  private translations: Record<SupportedLocale, Translations> = {
    en,
    tr,
    nl
  };

  private currentLocale: SupportedLocale = 'en';
  private currentTranslations: Translations = en;

  /**
   * Set the locale for the service
   * @param locale The locale to use
   */
  setLocale(locale: SupportedLocale = 'en'): void {
    this.currentLocale = locale;
    this.currentTranslations = this.translations[locale] || this.translations.en;
  }

  /**
   * Get the current locale
   */
  getLocale(): SupportedLocale {
    return this.currentLocale;
  }

  /**
   * Get a translation by key path
   * @param key The key path (e.g., 'buttons.save')
   * @param params Optional parameters for string interpolation
   */
  translate(key: string, params?: Record<string, any>): string {
    const keys = key.split('.');
    let result: any = this.currentTranslations;

    for (const k of keys) {
      if (result && typeof result === 'object' && k in result) {
        result = result[k];
      } else {
        console.warn(`Translation key not found: ${key}`);
        return key; // Return the key itself if translation is not found
      }
    }

    // Handle string interpolation
    if (typeof result === 'string' && params) {
      Object.keys(params).forEach(param => {
        result = result.replace(`{${param}}`, params[param]);
      });
    }

    return typeof result === 'string' ? result : key;
  }

  /**
   * Get all translations for the current locale
   */
  getTranslations(): Translations {
    return this.currentTranslations;
  }

  /**
   * Shorthand method for translate
   */
  t(key: string, params?: Record<string, any>): string {
    return this.translate(key, params);
  }
}